import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageIcon, Upload, Save } from 'lucide-react';
import Layout from '../../components/Layout';
import { companySettingsService } from '../../services/companySettingsService';
import { useCompany } from '../../context/CompanyContext';
import { UpdateCompanySettingsRequest, PrintOutputType, BillItemLineFormat, BillPaperSize } from '../../types';
import { parseApiError } from '../../utils/errorUtils';
import '../Products/ProductCategoryCreatePage.css';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'LKR', 'AUD', 'CAD', 'SGD', 'INR', 'CNY'];

const EMPTY_FORM: UpdateCompanySettingsRequest = {
  companyName: '',
  tinNo: '',
  logoUrl: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: '',
  phone: '',
  email: '',
  website: '',
  currencyCode: 'USD',
  financialYearStartMonth: 1,
  enablePurchaseOrderApproval: true,
  defaultApprovalLevel: 1,
  enableSalesInvoicePrinting: true,
  defaultPrintOutput: PrintOutputType.PDF,
  defaultPrinterName: '',
  printerPageWidth: 80,
  enableAutoSupplierCode: true,
  enableAutoCustomerCode: true,
  // Bill layout — managed on the dedicated Bill Printing page; carried
  // through here so saving this page never resets that configuration.
  billShowLogo: true,
  billHeaderLine1: '',
  billHeaderLine2: '',
  billFooterLine1: '',
  billFooterLine2: '',
  billShowPaymentMethod: true,
  billShowCompanyName: true,
  billShowAddress: true,
  billShowPhone: true,
  billShowEmail: false,
  billShowWebsite: false,
  billItemLineFormat: BillItemLineFormat.ONE_LINE,
  billShowItemCode: false,
  billShowQty: true,
  billShowUnitPrice: true,
  billShowLineTotal: true,
  billPaperSize: BillPaperSize.A4,
};

const CompanySettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { refreshCompany } = useCompany();

  const [formData, setFormData] = useState<UpdateCompanySettingsRequest>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadWarning, setLoadWarning] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [availablePrinters, setAvailablePrinters] = useState<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loadingPrinters, setLoadingPrinters] = useState(false);

  // ── Logo upload state ────────────────────────────────────────────────────
  type LogoStatus = 'idle' | 'uploading' | 'success' | 'error';
  const [logoStatus, setLogoStatus]   = useState<LogoStatus>('idle');
  const [logoError,  setLogoError]    = useState<string | null>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side validation
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setLogoStatus('error');
      setLogoError(t('companySetting.logoInvalidType'));
      e.target.value = '';
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setLogoStatus('error');
      setLogoError(t('companySetting.logoTooLarge'));
      e.target.value = '';
      return;
    }

    // Show instant local preview
    const objectUrl = URL.createObjectURL(file);
    setLocalPreview(objectUrl);
    setLogoStatus('uploading');
    setLogoError(null);

    try {
      const res = await companySettingsService.uploadLogo(file);
      const newUrl = res.data?.data?.logoUrl;
      if (newUrl) {
        setFormData((prev) => ({ ...prev, logoUrl: newUrl }));
        setLogoStatus('success');
        await refreshCompany(); // propagate to Sidebar immediately
      }
    } catch {
      setLogoStatus('error');
      setLogoError(t('companySetting.logoUploadError'));
      setLocalPreview(null);
    } finally {
      URL.revokeObjectURL(objectUrl);
      setLocalPreview(null);
      e.target.value = '';
    }
  };

  const handleRemoveLogo = () => {
    setFormData((prev) => ({ ...prev, logoUrl: '' }));
    setLogoStatus('idle');
    setLogoError(null);
  };

  useEffect(() => {
    setLoading(true);
    companySettingsService
      .getCompanySettings()
      .then((res) => {
        const d = res.data?.data;
        if (d) {
          setFormData({
            companyName: d.companyName ?? '',
            tinNo: d.tinNo ?? '',
            logoUrl: d.logoUrl ?? '',
            addressLine1: d.addressLine1 ?? '',
            addressLine2: d.addressLine2 ?? '',
            city: d.city ?? '',
            state: d.state ?? '',
            postalCode: d.postalCode ?? '',
            country: d.country ?? '',
            phone: d.phone ?? '',
            email: d.email ?? '',
            website: d.website ?? '',
            currencyCode: d.currencyCode ?? 'USD',
            financialYearStartMonth: d.financialYearStartMonth ?? 1,
            enablePurchaseOrderApproval: d.enablePurchaseOrderApproval ?? true,
            defaultApprovalLevel: d.defaultApprovalLevel ?? 1,
            enableSalesInvoicePrinting: d.enableSalesInvoicePrinting ?? true,
            defaultPrintOutput: d.defaultPrintOutput ?? PrintOutputType.PDF,
            defaultPrinterName: d.defaultPrinterName ?? '',
            printerPageWidth: d.printerPageWidth ?? 80,
            enableAutoSupplierCode: d.enableAutoSupplierCode ?? true,
            enableAutoCustomerCode: d.enableAutoCustomerCode ?? true,
            billShowLogo: d.billShowLogo ?? true,
            billHeaderLine1: d.billHeaderLine1 ?? '',
            billHeaderLine2: d.billHeaderLine2 ?? '',
            billFooterLine1: d.billFooterLine1 ?? '',
            billFooterLine2: d.billFooterLine2 ?? '',
            billShowPaymentMethod: d.billShowPaymentMethod ?? true,
            billShowCompanyName: d.billShowCompanyName ?? true,
            billShowAddress: d.billShowAddress ?? true,
            billShowPhone: d.billShowPhone ?? true,
            billShowEmail: d.billShowEmail ?? false,
            billShowWebsite: d.billShowWebsite ?? false,
            billItemLineFormat: d.billItemLineFormat ?? BillItemLineFormat.ONE_LINE,
            billShowItemCode: d.billShowItemCode ?? false,
            billShowQty: d.billShowQty ?? true,
            billShowUnitPrice: d.billShowUnitPrice ?? true,
            billShowLineTotal: d.billShowLineTotal ?? true,
            billPaperSize: d.billPaperSize ?? BillPaperSize.A4,
          });
        }
        // d === null means no settings saved yet — form shows with defaults, no warning needed
      })
      .catch(() => {
        // Non-blocking: form still renders with defaults so user can configure and save
        setLoadWarning(t('companySetting.loadWarning'));
      })
      .finally(() => setLoading(false));

    // Load available printers
    setLoadingPrinters(true);
    companySettingsService
      .getAvailablePrinters()
      .then((res) => {
        const printers = res.data?.printers ?? [];
        setAvailablePrinters(printers);
      })
      .catch(() => {
        // Non-blocking: allow manual printer entry if detection fails
        setAvailablePrinters([]);
      })
      .finally(() => setLoadingPrinters(false));
  }, [t]);

  // Fields that must be stored as numbers even though they come from <select>
  const NUMERIC_SELECT_FIELDS = new Set(['defaultPrintOutput', 'printerPageWidth', 'financialYearStartMonth', 'defaultApprovalLevel']);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked
        : (type === 'number' || NUMERIC_SELECT_FIELDS.has(name)) ? Number(value)
        : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.companyName.trim()) {
      setError(t('companySetting.companyNameRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await companySettingsService.updateCompanySettings(formData);
      // Refresh the app-wide context so logo + currency update immediately everywhere
      await refreshCompany();
      setSuccessMessage(t('companySetting.savedSuccess'));
    } catch (err: unknown) {
      setError(parseApiError(err, t('companySetting.failedToSave')).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="create-container">
          <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
            <div className="spinner-border" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="create-container">
        <div className="create-header">
          <h1 className="create-title">{t('companySetting.title')}</h1>
          <p className="create-subtitle">{t('companySetting.subtitle')}</p>
        </div>

        {loadWarning && (
          <div className="alert alert-warning alert-dismissible fade show">
            {loadWarning}
            <button type="button" className="btn-close" onClick={() => setLoadWarning(null)} />
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show">
            <strong>{t('common.success')}:</strong> {successMessage}
            <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)} />
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)} />
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="create-content">
            {/* ── Left column ── */}
            <div className="create-main-section">

              {/* Company Identity */}
              <div className="create-card">
                <div className="create-card-header">
                  <h2 className="create-card-title">{t('companySetting.companyInfo')}</h2>
                </div>
                <div className="create-card-body">
                  <div className="form-group">
                    <label className="form-label">
                      {t('companySetting.companyName')} <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="e.g. Acme Corporation Ltd."
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('companySetting.tinNo')}</label>
                    <input
                      type="text"
                      className="form-input"
                      name="tinNo"
                      value={formData.tinNo ?? ''}
                      onChange={handleChange}
                      placeholder="e.g. TIN-123456789"
                    />
                  </div>
                  {/* ── Company Logo Upload ── */}
                  <div className="form-group">
                    <label className="form-label">{t('companySetting.logo')}</label>

                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>

                      {/* Preview box */}
                      <div style={{
                        width: '96px', height: '96px',
                        border: '2px dashed var(--border-color)',
                        borderRadius: '10px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        overflow: 'hidden', flexShrink: 0,
                        background: 'var(--bg-secondary)',
                      }}>
                        {(localPreview || formData.logoUrl) ? (
                          <img
                            src={localPreview || formData.logoUrl || undefined}
                            alt="Company logo preview"
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        ) : (
                          <ImageIcon size={32} strokeWidth={1.5} stroke="var(--text-secondary)" />
                        )}
                      </div>

                      {/* Controls */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            type="button"
                            className="btn-save"
                            style={{ padding: '7px 14px', fontSize: '13px' }}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={logoStatus === 'uploading'}
                          >
                            {logoStatus === 'uploading' ? (
                              <><span className="btn-spinner" />{t('companySetting.logoUploading')}</>
                            ) : (
                              <>
                                <Upload size={14} strokeWidth={2} />
                                {t('companySetting.logoChangeBtnLabel')}
                              </>
                            )}
                          </button>

                          {formData.logoUrl && logoStatus !== 'uploading' && (
                            <button
                              type="button"
                              className="btn-delete"
                              style={{ padding: '7px 14px', fontSize: '13px' }}
                              onClick={handleRemoveLogo}
                            >
                              {t('companySetting.logoRemoveBtnLabel')}
                            </button>
                          )}
                        </div>

                        <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>
                          {t('companySetting.logoHint')}
                        </p>

                        {logoStatus === 'success' && (
                          <p style={{ fontSize: '12px', color: 'var(--color-success)', margin: 0 }}>
                            ✓ {t('companySetting.logoUploadSuccess')}
                          </p>
                        )}
                        {logoStatus === 'error' && logoError && (
                          <p style={{ fontSize: '12px', color: 'var(--color-danger)', margin: 0 }}>
                            {logoError}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Hidden file input */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      style={{ display: 'none' }}
                      onChange={handleLogoFileChange}
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="create-card">
                <div className="create-card-header">
                  <h2 className="create-card-title">{t('companySetting.address')}</h2>
                </div>
                <div className="create-card-body">
                  <div className="form-group">
                    <label className="form-label">{t('companySetting.addressLine1')}</label>
                    <input
                      type="text"
                      className="form-input"
                      name="addressLine1"
                      value={formData.addressLine1 ?? ''}
                      onChange={handleChange}
                      placeholder="Street address"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('companySetting.addressLine2')}</label>
                    <input
                      type="text"
                      className="form-input"
                      name="addressLine2"
                      value={formData.addressLine2 ?? ''}
                      onChange={handleChange}
                      placeholder="Apt, suite, floor, etc."
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label className="form-label">{t('companySetting.city')}</label>
                      <input
                        type="text"
                        className="form-input"
                        name="city"
                        value={formData.city ?? ''}
                        onChange={handleChange}
                        placeholder="City"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t('companySetting.state')}</label>
                      <input
                        type="text"
                        className="form-input"
                        name="state"
                        value={formData.state ?? ''}
                        onChange={handleChange}
                        placeholder="State / Province"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t('companySetting.postalCode')}</label>
                      <input
                        type="text"
                        className="form-input"
                        name="postalCode"
                        value={formData.postalCode ?? ''}
                        onChange={handleChange}
                        placeholder="Postal code"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t('companySetting.country')}</label>
                      <input
                        type="text"
                        className="form-input"
                        name="country"
                        value={formData.country ?? ''}
                        onChange={handleChange}
                        placeholder="Country"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="create-card">
                <div className="create-card-header">
                  <h2 className="create-card-title">{t('companySetting.contact')}</h2>
                </div>
                <div className="create-card-body">
                  <div className="form-group">
                    <label className="form-label">{t('companySetting.phone')}</label>
                    <input
                      type="tel"
                      className="form-input"
                      name="phone"
                      value={formData.phone ?? ''}
                      onChange={handleChange}
                      placeholder="+1 555 000 0000"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('companySetting.email')}</label>
                    <input
                      type="email"
                      className="form-input"
                      name="email"
                      value={formData.email ?? ''}
                      onChange={handleChange}
                      placeholder="info@company.com"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('companySetting.website')}</label>
                    <input
                      type="url"
                      className="form-input"
                      name="website"
                      value={formData.website ?? ''}
                      onChange={handleChange}
                      placeholder="https://www.company.com"
                    />
                  </div>
                </div>
              </div>

              {/* Financial */}
              <div className="create-card">
                <div className="create-card-header">
                  <h2 className="create-card-title">{t('companySetting.financial')}</h2>
                </div>
                <div className="create-card-body">
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label className="form-label">{t('companySetting.currency')}</label>
                      <select
                        className="form-input"
                        name="currencyCode"
                        value={formData.currencyCode}
                        onChange={handleChange}
                      >
                        {CURRENCIES.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">{t('companySetting.financialYearStart')}</label>
                      <select
                        className="form-input"
                        name="financialYearStartMonth"
                        value={formData.financialYearStartMonth}
                        onChange={handleChange}
                      >
                        {MONTHS.map((m, i) => (
                          <option key={i + 1} value={i + 1}>{m}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Approval Setup */}
              <div className="create-card">
                <div className="create-card-header">
                  <h2 className="create-card-title">{t('companySetting.approvalSetup')}</h2>
                </div>
                <div className="create-card-body">
                  <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <input
                      type="checkbox"
                      id="enablePOApproval"
                      name="enablePurchaseOrderApproval"
                      checked={formData.enablePurchaseOrderApproval}
                      onChange={handleChange}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <label htmlFor="enablePOApproval" className="form-label" style={{ margin: 0, cursor: 'pointer' }}>
                      {t('companySetting.enablePOApproval')}
                    </label>
                  </div>
                  <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <input
                      type="checkbox"
                      id="enableAutoSupplierCode"
                      name="enableAutoSupplierCode"
                      checked={formData.enableAutoSupplierCode}
                      onChange={handleChange}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <label htmlFor="enableAutoSupplierCode" className="form-label" style={{ margin: 0, cursor: 'pointer' }}>
                      {t('companySetting.enableAutoSupplierCode')}
                    </label>
                  </div>
                  <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <input
                      type="checkbox"
                      id="enableAutoCustomerCode"
                      name="enableAutoCustomerCode"
                      checked={formData.enableAutoCustomerCode}
                      onChange={handleChange}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <label htmlFor="enableAutoCustomerCode" className="form-label" style={{ margin: 0, cursor: 'pointer' }}>
                      {t('companySetting.enableAutoCustomerCode')}
                    </label>
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('companySetting.defaultApprovalLevel')}</label>
                    <input
                      type="number"
                      className="form-input"
                      name="defaultApprovalLevel"
                      min={1}
                      max={5}
                      value={formData.defaultApprovalLevel}
                      onChange={handleChange}
                    />
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      {t('companySetting.approvalLevelHint')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Print Configuration */}
              <div className="create-card">
                <div className="create-card-header">
                  <h2 className="create-card-title">{t('companySetting.printConfig')}</h2>
                </div>
                <div className="create-card-body">

                  {/* Print output mode */}
                  <div className="form-group">
                    <label className="form-label">{t('companySetting.defaultPrintOutput')}</label>
                    <select
                      className="form-input"
                      name="defaultPrintOutput"
                      value={formData.defaultPrintOutput}
                      onChange={handleChange}
                    >
                      <option value={0}>{t('companySetting.printOutputPdf')}</option>
                      <option value={1}>{t('companySetting.printOutputPos')}</option>
                      <option value={2}>{t('companySetting.printOutputBoth')}</option>
                    </select>
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                      {t('companySetting.printOutputHint')}
                    </p>
                  </div>

                  {/* Printer name — shown only when POS or BOTH is selected */}
                  {(formData.defaultPrintOutput === 1 || formData.defaultPrintOutput === 2) && (
                    <div className="form-group">
                      <label className="form-label">{t('companySetting.defaultPrinterName')}</label>
                      <input
                        type="text"
                        className="form-input"
                        name="defaultPrinterName"
                        value={formData.defaultPrinterName ?? ''}
                        onChange={handleChange}
                        placeholder={t('companySetting.printerNamePlaceholder')}
                      />
                      <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                        {t('companySetting.printerNameHint')}
                      </p>
                    </div>
                  )}

                  {/* Page width */}
                  <div className="form-group">
                    <label className="form-label">{t('companySetting.printerPageWidth')}</label>
                    <select
                      className="form-input"
                      name="printerPageWidth"
                      value={formData.printerPageWidth}
                      onChange={handleChange}
                    >
                      <option value={58}>58 mm</option>
                      <option value={80}>80 mm</option>
                    </select>
                  </div>

                </div>
              </div>

            </div>

            {/* ── Right sidebar ── */}
            <div className="create-sidebar">
              <div className="create-card">
                <div className="create-card-header">
                  <h3 className="create-card-title">{t('companySetting.saveSettings')}</h3>
                </div>
                <div className="create-card-body">
                  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                    {t('companySetting.saveSidebarNote')}
                  </p>
                  <div className="form-actions" style={{ flexDirection: 'column' }}>
                    <button type="submit" className="btn-save" disabled={saving}>
                      {saving ? (
                        <>
                          <span className="btn-spinner" />
                          {t('common.saving')}
                        </>
                      ) : (
                        <>
                          <Save size={16} strokeWidth={2} />
                          {t('common.saveChanges')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="create-card">
                <div className="create-card-header">
                  <h3 className="create-card-title">{t('companySetting.requiredFields')}</h3>
                </div>
                <div className="create-card-body">
                  <ul style={{ paddingInlineStart: '16px', margin: 0, fontSize: '13px', lineHeight: '1.8' }}>
                    <li>{t('companySetting.companyName')}</li>
                  </ul>
                </div>
              </div>

              <div className="create-card">
                <div className="create-card-header">
                  <h3 className="create-card-title">{t('companySetting.approvalNote')}</h3>
                </div>
                <div className="create-card-body">
                  <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.6', margin: 0 }}>
                    {t('companySetting.approvalNoteText')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CompanySettingsPage;
